import {
  cloneElement,
  isValidElement,
  useMemo,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { cn, flattenStyles, type PressableStateType } from "../../lib";

/**
 * Slot's merge contract, shared by the web and native Slot components.
 *
 * Slot clones its single child and merges its own props into the child's:
 *
 * - **Handlers** (`on*`): when both define one, the child's runs first, then
 *   the slot's, each exactly once.
 * - **className**: `cn(slot, child)`. If either side is a state callback, the
 *   result is a callback that resolves both; Slot never evaluates it, so the
 *   child must be callback-capable (a Luce Pressable) to use one.
 * - **style**: combined whenever either side supplies one. Static styles are
 *   flattened slot first, child last (nested and falsy arrays included); a
 *   state callback on either side yields a callback that resolves and
 *   flattens both. Slot never evaluates it.
 * - **ref**: the ref given to Slot and the child's own ref (`child.props.ref`)
 *   both receive the child's instance. Callback refs that return a cleanup get
 *   that cleanup on detach; others are called with `null`; object refs are
 *   reset to `null`. The composed ref keeps its identity while both refs are
 *   unchanged, so a rerender doesn't detach and reattach. The child must accept
 *   a ref (React 19 ref-as-prop or `forwardRef`) and attach it to its host.
 * - **accessibilityActions**: both sides' actions, the child's first; a slot
 *   action whose name the child already uses is dropped. Their handlers
 *   (`onAccessibilityAction`) compose like any other.
 * - **Everything else**: the child's value wins.
 */

type AnyProps = Record<string, unknown>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyStyle = any;
type ClassName = string | ((state: PressableStateType) => string) | undefined;
type RefCleanup = () => void;

function setRef<T>(ref: Ref<T> | undefined, value: T | null): void | RefCleanup {
  if (typeof ref === "function") return ref(value) as void | RefCleanup;
  if (ref != null) (ref as { current: T | null }).current = value;
}

/**
 * Compose refs into one callback ref. On detach it runs each callback ref's
 * returned cleanup, or calls it with `null` when it returned none, and resets
 * object refs to `null` (React 19 ref cleanup).
 */
export function composeRefs<T>(...refs: (Ref<T> | undefined)[]) {
  return (node: T): RefCleanup => {
    const cleanups = refs.map((ref) => setRef(ref, node));
    return () => {
      refs.forEach((ref, i) => {
        const cleanup = cleanups[i];
        if (typeof cleanup === "function") cleanup();
        else setRef(ref, null);
      });
    };
  };
}

const isHandler = (name: string) => /^on[A-Z]/.test(name);

function composeHandlers(slotHandler: unknown, childHandler: unknown) {
  if (typeof slotHandler !== "function") return childHandler;
  if (typeof childHandler !== "function") return slotHandler;
  return (...args: unknown[]) => {
    (childHandler as (...a: unknown[]) => void)(...args);
    (slotHandler as (...a: unknown[]) => void)(...args);
  };
}

/** Merge className, composing state callbacks instead of evaluating them. */
export function combineClassNames(slotClassName: ClassName, childClassName: ClassName): ClassName {
  if (typeof slotClassName === "function" || typeof childClassName === "function") {
    return (state: PressableStateType) =>
      cn(
        typeof slotClassName === "function" ? slotClassName(state) : slotClassName,
        typeof childClassName === "function" ? childClassName(state) : childClassName,
      );
  }
  return cn(slotClassName, childClassName);
}

/**
 * Combine styles, slot first and child last. Static styles are flattened; a
 * state callback on either side yields a callback that resolves and flattens
 * both. The callback is composed here, never evaluated.
 */
export function combineStyles(slotStyle?: AnyStyle, childStyle?: AnyStyle): AnyStyle {
  if (!slotStyle && !childStyle) return undefined;

  if (typeof slotStyle === "function" || typeof childStyle === "function") {
    return (state: PressableStateType) =>
      flattenStyles([
        typeof slotStyle === "function" ? slotStyle(state) : slotStyle,
        typeof childStyle === "function" ? childStyle(state) : childStyle,
      ]);
  }

  return flattenStyles([slotStyle, childStyle]);
}

type AccessibilityAction = { name: string; label?: string };

function combineAccessibilityActions(slotActions: unknown, childActions: unknown) {
  const child = (childActions ?? []) as AccessibilityAction[];
  const slot = (slotActions ?? []) as AccessibilityAction[];
  const taken = new Set(child.map((action) => action.name));
  return [...child, ...slot.filter((action) => !taken.has(action.name))];
}

/** Merge slot props into child props by the contract above. */
export function mergeProps(slotProps: AnyProps, childProps: AnyProps): AnyProps {
  const merged: AnyProps = { ...slotProps, ...childProps };

  for (const name of new Set([...Object.keys(slotProps), ...Object.keys(childProps)])) {
    if (isHandler(name)) {
      merged[name] = composeHandlers(slotProps[name], childProps[name]);
    } else if (name === "style") {
      merged[name] = combineStyles(slotProps[name], childProps[name]);
    } else if (name === "className") {
      merged[name] = combineClassNames(slotProps[name] as ClassName, childProps[name] as ClassName);
    } else if (name === "accessibilityActions") {
      merged[name] = combineAccessibilityActions(slotProps[name], childProps[name]);
    }
  }

  return merged;
}

/**
 * Clone `children` with the slot's props merged in and the refs composed.
 * Shared by every Slot component on both platforms. Returns `null` (with a
 * warning) when `children` isn't a single valid element.
 */
export function useSlot(
  name: string,
  children: ReactNode,
  slotProps: AnyProps,
  forwardedRef: Ref<unknown> | undefined,
): ReactElement | null {
  const child = isValidElement(children) ? (children as ReactElement<AnyProps>) : null;
  const childRef = child?.props["ref"] as Ref<unknown> | undefined;
  const ref = useMemo(
    () => (forwardedRef || childRef ? composeRefs(forwardedRef, childRef) : undefined),
    [forwardedRef, childRef],
  );

  if (!child) {
    console.warn(`${name} - Invalid asChild element`, children);
    return null;
  }

  return cloneElement(child, { ...mergeProps(slotProps, child.props), ref } as AnyProps);
}

/**
 * Check if children are text-only (string or array of strings).
 */
export function isTextChildren(
  children: ReactNode | ((state: PressableStateType) => ReactNode),
): boolean {
  if (typeof children === "function") {
    return false;
  }

  return Array.isArray(children)
    ? children.every((child) => typeof child === "string")
    : typeof children === "string";
}
